import { ChangeDetectorRef, Component, Inject } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { EventType } from '@app/core/model/enums/event-type.enum'
import { ScopeVersion } from '@app/core/model/scope-version'
import { LanguageService } from '@app/core/service/language.service'
import { NotificationEvent, RegisteredSubscription, WebsocketService } from '@app/shared/services/websocket.service'
import { untilDestroyed } from '@app/shared/utils/utils'
import { Store } from '@ngrx/store'
import { plainToInstance } from 'class-transformer'
import { every } from 'lodash'
import { FolderOverviewSelectors } from '../store'
import { FolderTrafficStatus } from '../store/models/folder-traffic-status.interface'
import { FolderTrafficModalConfig } from './folder-traffic-modal-config'

@Component({
  selector: 'folder-traffic-modal',
  templateUrl: './folder-traffic-modal.component.html',
  styleUrls: ['./folder-traffic-modal.component.scss'],
})
export class FolderTrafficModalComponent {
  scopes: ScopeVersion[] = []
  searchString: string

  title = ''
  description = ''

  isTrafficking: boolean
  trafficStatus: FolderTrafficStatus
  registeredSubs: RegisteredSubscription[] = []
  completed = new Map()
  failed = new Map()
  isDone: boolean

  get isAllSelected() {
    return every(this.scopes, { selected: true })
  }

  get isDisabled() {
    return this.getSelectedScopes?.length === 30
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FolderTrafficModalConfig,
    public dialogRef: MatDialogRef<FolderTrafficModalComponent>,
    public dialog: MatDialog,
    public cdr: ChangeDetectorRef,
    public store$: Store,
    public lang: LanguageService,
    private webSocketService: WebsocketService
  ) {
    this.destroy$ = untilDestroyed()

    this.title = `Export to ${data.trafficSystemName}`
    this.description = `Select ${this.lang.get('scope.p')} to traffic`

    this.scopes = [...data.trafficableScopes].map((s) => plainToInstance(ScopeVersion, { ...s, selected: false }))

    this.store$
      .select(FolderOverviewSelectors.selectTrafficStatus)
      .pipe(this.destroy$())
      .subscribe((status: FolderTrafficStatus) => {
        if (status) {
          Object.entries(status).forEach(([key, value]) => {
            if (value.errorType) {
              this.failed.set(key, value)
              status = {
                ...status,
                [key]: { ...status[key], error: this.getServerErrorMessage(value.errorType), failed: true },
              }
            }
          })
          this.trafficStatus = status
          console.info(this.trafficStatus)
        }
      })

    if (this.registeredSubs.length == 0)
      this.registeredSubs.push(
        this.webSocketService.registerSowEventReceiver(this.data.folder.identity.id, (event: any) =>
          this.onFolderEventReceived.call(this, event)
        )
      )
  }

  traffic() {
    const scopeIds = this.getSelectedScopes().map((scope) => scope.identity.id)
    scopeIds?.length ? this.data.onTraffic(scopeIds) : this.dialogRef.close()
    this.isTrafficking = true
    this.title = 'Trafficking in progress'
    this.description = 'Currently trafficking.'
  }

  onFolderEventReceived(event: NotificationEvent) {
    switch (event.type) {
      case EventType.SCOPE_EXTERNAL_TRANSFER_ABORTED: {
        const obj = {
          processing: false,
          errorType: event.errorType,
          error: this.getServerErrorMessage(event.errorType),
          failed: true,
        }
        this.failed.set(event.object.scopeId.id, obj)
        this.trafficStatus[event.object.scopeId.id] = obj
        break
      }
      case EventType.SCOPE_EXTERNAL_TRANSFER_COMPLETE: {
        const obj = { processing: false, completed: true }
        this.completed.set(event.object.scopeId.id, obj)
        this.trafficStatus[event.object.scopeId.id] = obj
        break
      }
    }
    this.checkIfDone()
  }

  checkIfDone() {
    this.isDone = this.isTraffickingDone()
    if (this.isDone) {
      this.title = this.failed ? 'Completed with errors' : 'Completed successfully'
      this.description = this.failed
        ? 'There are some errors encountered with trafficking.'
        : 'All scopes traffic successfully.'
    }
  }

  getServerErrorMessage(errorType) {
    const scopeTitle = this.lang.get('scope')
    switch (errorType) {
      case 'SCOPE_INCOMPLETE':
        return `${this.lang.get('deliverable.p')} in this ${scopeTitle} are incomplete.`
      case 'SCOPE_ALREADY_IN_SYNC':
        return `This ${scopeTitle} has no changes.`
      case 'SCOPE_STATUS_NON_TRAFFICABLE':
        return `This ${scopeTitle} cannot be trafficked at this approval status.`
      case 'SCOPE_NOT_TRAFFICABLE':
      case 'TRAFFIC_SYSTEM_NOT_CONFIGURED':
      case 'INACTIVE_CONNECTIONS':
      case 'CONNECTION_NOT_FOUND':
        return `This ${scopeTitle} cannot be trafficked.`
      case 'EXTERNAL':
        return `This ${scopeTitle} cannot be trafficked.
          If the problem persists, please contact Scope support.`
      default:
        return 'Please contact Scope support.'
    }
  }

  isTraffickingDone = () =>
    this.trafficStatus && Object.keys(this.trafficStatus).length == this.failed.size + this.completed.size

  onSearch = (value: string) => (this.searchString = value)

  selectAll = () =>
    ((value) => {
      this.scopes?.map((g) => (this.isDisabled ? g : (g.selected = !value)))
      this.cdr.detectChanges()
    })(this.isAllSelected)

  getSelectedScopes = () => this.scopes.filter((scope) => scope.selected)

  optionFunction = (scope: ScopeVersion) => scope.name

  readonly MAX_SELECTED_SCOPES = 30
  private readonly destroy$
  protected readonly trackById = (index: number, item: any) => item.identity.id
}
